import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';

import { bemCssModules, getTestId } from '@utils';
import { useInterval, useNavigate } from '@utils/hooks';

import { accountsActions, useStore, walletActions } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { IbanStatusesEnum } from '@modules/user/types';

import LoaderLayout from '@layouts/LoaderLayout';

import Accounts from './Accounts';
import Search from './Search';
import Section from './Section';

import style from './Wallets.scss';

const cn = bemCssModules(style, 'Wallets');
const ti = getTestId('Wallets');

const GET_WALLET_INTERVAL = 30000;

export type TWallets = {
  isDetailPage?: boolean;
};

const Wallets: CustomFC<TWallets> = ({ isDetailPage = false, className }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const isProfilePage = useMatch(CONST.nav.profile);
  const [filterValue, setFilterValue] = useState('');
  const [isShowAll, setShowAll] = useState(isDetailPage);

  const [{ global, wallet, user, accounts }, dispatch] = useStore();
  const currencies = global.currencies.crypto;
  const isLoading =
    (!!wallet.loaders.getWalletPolling || !!wallet.loaders.getWallet) && !wallet.data;
  const isAccountsLoading =
    (!!accounts.loaders.getAccountsPolling || !!accounts.loaders.getAccounts) &&
    !accounts.details.length;
  const isIbanCreated = user.data?.iban_status === IbanStatusesEnum.created;
  const accountsBalance = accounts.details[0]?.fiat_balance || '0';
  const ibanAccountNumber = accounts.details[0]?.iban || '';
  const displayOptions = global.currencies.config.display_options;

  useEffect(() => {
    if (isIbanCreated) dispatch(accountsActions.getAccounts());
  }, [user.data]);

  useInterval({
    callback: () => {
      dispatch(walletActions.getWalletPolling());
      if (isIbanCreated) dispatch(accountsActions.getAccountsPolling());
    },
    delay: GET_WALLET_INTERVAL,
  });

  const filterCurrencies = (curs: CryptoCurrencyEnum[]): CryptoCurrencyEnum[] =>
    curs.filter(currency => {
      const currencyValue = currency.toLocaleLowerCase();
      const filter = filterValue.toLowerCase();
      const currencyName = (displayOptions[currency]?.fullname || currency).toLocaleLowerCase();

      return currencyName.includes(filter) || currencyValue.includes(filter);
    });

  const handleShowAll = () => setShowAll(prev => !prev);

  const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilterValue(event.target.value);

  const sortedList: CryptoCurrencyEnum[] = global.currencies.crypto
    .map(currency => currency)
    .sort((currencyA, currencyB) => {
      const cryptoFullnameA = displayOptions[currencyA]?.fullname || currencyA;
      const cryptoFullnameB = displayOptions[currencyB]?.fullname || currencyB;
      const cryptoA = wallet?.data?.[currencyA].balance || '0';
      const cryptoB = wallet?.data?.[currencyB].balance || '0';
      const fiatA = wallet?.data?.[currencyA].fiat_balance || '0';
      const fiatB = wallet?.data?.[currencyB].fiat_balance || '0';

      if (+fiatA > +fiatB) return -1;
      if (+fiatA < +fiatB) return 1;

      if (!!+cryptoA && !+cryptoB) return -1;
      if (!+cryptoA && !!+cryptoB) return 1;

      return cryptoFullnameA.localeCompare(cryptoFullnameB);
    });

  const currenciesWithBalance: CryptoCurrencyEnum[] = global.currencies.crypto.filter(
    (currency: CryptoCurrencyEnum) => Number(wallet?.data?.[currency].balance) !== 0
  );

  const popularCurrencies: CryptoCurrencyEnum[] = currencies.filter(
    currency =>
      !currenciesWithBalance.includes(currency) && CONST.popularCurrencies.includes(currency)
  );

  const limit =
    currenciesWithBalance.length > CONST.numberWalletsToShow
      ? currenciesWithBalance.length
      : CONST.numberWalletsToShow;

  const list: CryptoCurrencyEnum[] = sortedList.slice(0, isShowAll ? currencies.length : limit);

  const allCurrencies: CryptoCurrencyEnum[] = filterValue.length > 0 ? sortedList : list;

  const filteredCurrenciesWithBalance = filterCurrencies(currenciesWithBalance);
  const filteredPopularCurrencies = filterCurrencies(popularCurrencies);
  const filteredAllCurrencies = filterCurrencies(allCurrencies);

  const isFoundBySearch =
    !!filteredCurrenciesWithBalance.length ||
    !!filteredPopularCurrencies.length ||
    !!filteredAllCurrencies.length;

  const goWalletsDetails = () => navigate(CONST.nav.balances);

  const isShowExtraInfo = !isDetailPage && !filterValue;

  if (currenciesWithBalance.length === 0 && isProfilePage) return null;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <LoaderLayout isLoading={isLoading}>
        <Search isDetailPage={isDetailPage} onChange={handleFilter} />
        {isFoundBySearch ? (
          <>
            <div className={cn('content')}>
              {isShowExtraInfo && <h2 className={cn('title')}>{t('menu.wallets')}</h2>}
              {isShowAll && !!filteredPopularCurrencies.length && (
                <>
                  <Section
                    title={t('wallets.my_wallets')}
                    currencies={filteredCurrenciesWithBalance}
                  />
                  <Section
                    title={t('wallets.popular_crypto')}
                    currencies={filteredPopularCurrencies}
                  />
                </>
              )}

              {!!filteredAllCurrencies.length && (
                <Section
                  title={isShowAll ? t('wallets.all_crypto') : ''}
                  currencies={filteredAllCurrencies}
                />
              )}

              {isShowExtraInfo && (
                <>
                  <button className={cn('button')} type="button" onClick={handleShowAll}>
                    {isShowAll ? t('wallets.show_less') : t('wallets.show_all')}
                  </button>
                  <button className={cn('mobile-button')} type="button" onClick={goWalletsDetails}>
                    {t('wallets.show_all')}
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <p className={cn('empty')}>{t('wallets.search_empty')}</p>
        )}
      </LoaderLayout>
      {isIbanCreated && (
        <LoaderLayout className={cn('accounts-loader')} isLoading={isAccountsLoading}>
          <div className={cn('accounts')}>
            <h2 className={cn('accounts-title')}>{t('menu.accounts')}</h2>
            <div className={cn('accounts-block')}>
              <Accounts
                balance={accountsBalance}
                accountNumber={ibanAccountNumber}
                isAccountCreated={isIbanCreated}
              />
            </div>
          </div>
        </LoaderLayout>
      )}
    </div>
  );
};

export default Wallets;
